/**
 * Assistant, assistant-meta, changed-files, and reasoning-trace rows
 * (ticket 3525, upstream pin e3e7cc3f, MIT), ported from upstream
 * `MessagesTimeline.tsx`. Forge deltas: citation content, changed-files card,
 * and terminal-context chips keep forge's own surfaces; the thought preview
 * uses a structural markdown walk instead of upstream's remark plugin.
 *
 * @module TimelineAssistantRows
 */

import { use, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { TurnId } from "@cmmd-forge/contracts";
import { BrainIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { AssistantMessageMeta } from "./TimelineAssistantMeta";
import ChatMarkdown from "../ChatMarkdown";
import { AssistantCitationContent } from "./AssistantCitationContent";
import { ChangedFilesCard } from "./ChangedFilesCard";
import { shouldAutoExpandChangedFiles } from "./changedFilesPresentation";
import { useUiStateStore } from "~/uiStateStore";
import type { TurnDiffSummary } from "../../types";
import { observeVisibleAnimation } from "~/lib/visibleAnimation";
import { ActivityShimmerOverlay } from "./TimelineLiveActivityRow";
import { TimelineRowCtx, TimelineRowActivityCtx } from "./TimelineRowContext";
import type { MessagesTimelineRow } from "./MessagesTimeline.rowTypes";

// Screen readers skim a transcript by heading, so every message announces its
// author as one. Visually hidden and excluded from selection.
function MessageAuthorHeading({ children }: { children: string }) {
  return <h3 className="sr-only select-none">{children}</h3>;
}

export function AssistantTimelineRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "message" }>;
}) {
  const ctx = use(TimelineRowCtx);
  const messageText = row.message.text || (row.message.streaming ? "" : "(empty response)");

  return (
    <div className="min-w-0 px-1 py-0.5">
      <MessageAuthorHeading>Forge</MessageAuthorHeading>
      <AssistantCitationContent
        routeThreadKey={ctx.routeThreadKey}
        messageId={row.message.id}
        sourceEventId={row.message.sourceEventId}
        itemKey={row.id}
      >
        <ChatMarkdown
          text={messageText}
          cwd={ctx.markdownCwd}
          threadKey={ctx.routeThreadKey}
          skills={ctx.providerSkills}
          isStreaming={Boolean(row.message.streaming)}
        />
      </AssistantCitationContent>
      <AssistantChangedFilesSection
        turnSummary={row.assistantTurnDiffSummary}
        routeThreadKey={ctx.routeThreadKey}
        resolvedTheme={ctx.resolvedTheme}
        onOpenTurnDiff={ctx.onOpenTurnDiff}
      />
      {row.showAssistantMeta ? (
        <AssistantMessageMeta
          className="mt-1.5"
          message={row.message}
          showCopyButton={row.showAssistantCopyButton}
          copyStreaming={row.assistantCopyStreaming}
        />
      ) : null}
    </div>
  );
}

export function AssistantMetaTimelineRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "assistant-meta" }>;
}) {
  return (
    <div className="px-1">
      <AssistantMessageMeta
        className="mt-0.5"
        message={row.message}
        showCopyButton={row.showAssistantCopyButton}
        copyStreaming={row.assistantCopyStreaming}
        alwaysVisible
      />
    </div>
  );
}

/** Subscribes to the UI state store, so toggling re-renders only this row. */
const AssistantChangedFilesSection = function AssistantChangedFilesSection({
  turnSummary,
  routeThreadKey,
  resolvedTheme,
  onOpenTurnDiff,
}: {
  turnSummary: TurnDiffSummary | undefined;
  routeThreadKey: string;
  resolvedTheme: "light" | "dark";
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
}) {
  const ctx = use(TimelineRowCtx);
  if (!turnSummary) return null;
  const checkpointFiles = turnSummary.files;
  if (checkpointFiles.length === 0) return null;

  return (
    <AssistantChangedFilesSectionInner
      turnSummary={turnSummary}
      checkpointFiles={checkpointFiles}
      routeThreadKey={routeThreadKey}
      resolvedTheme={resolvedTheme}
      latestTurnDiffId={ctx.latestTurnDiffId}
      onOpenTurnDiff={onOpenTurnDiff}
    />
  );
};

function AssistantChangedFilesSectionInner({
  turnSummary,
  checkpointFiles,
  routeThreadKey,
  resolvedTheme,
  latestTurnDiffId,
  onOpenTurnDiff,
}: {
  turnSummary: TurnDiffSummary;
  checkpointFiles: TurnDiffSummary["files"];
  routeThreadKey: string;
  resolvedTheme: "light" | "dark";
  latestTurnDiffId: string | null;
  onOpenTurnDiff: (turnId: TurnId, filePath?: string) => void;
}) {
  const isLatestTurn = latestTurnDiffId === turnSummary.turnId;
  const persistedExpanded = useUiStateStore(
    (store) => store.threadChangedFilesExpandedById[routeThreadKey]?.[turnSummary.turnId],
  );
  const setExpanded = useUiStateStore((store) => store.setThreadChangedFilesExpanded);
  const autoExpanded = shouldAutoExpandChangedFiles(checkpointFiles, isLatestTurn);
  const [allDirectoriesExpanded, setAllDirectoriesExpanded] = useState(autoExpanded);
  const expanded = persistedExpanded ?? autoExpanded;

  return (
    <ChangedFilesCard
      turnId={turnSummary.turnId}
      files={checkpointFiles}
      expanded={expanded}
      showCompactPreview={isLatestTurn}
      allDirectoriesExpanded={allDirectoriesExpanded}
      resolvedTheme={resolvedTheme}
      onExpandedChange={(nextExpanded) =>
        setExpanded(routeThreadKey, turnSummary.turnId, nextExpanded)
      }
      onToggleAllDirectories={() => setAllDirectoriesExpanded((current) => !current)}
      onOpenTurnDiff={onOpenTurnDiff}
    />
  );
}

type MdNode = {
  type: string;
  value?: string;
  alt?: string;
  children?: MdNode[];
};

function markdownPlainText(node: MdNode): string {
  if (node.type === "html" || node.type === "definition") return "";
  if (node.alt !== undefined) return node.alt ?? "";
  if (node.value !== undefined) return node.value;
  if (node.children) {
    const separator = ["root", "blockquote", "list", "listItem", "table", "tableRow"].includes(
      node.type,
    )
      ? " "
      : "";
    return node.children.map(markdownPlainText).join(separator);
  }
  return node.type === "break" ? " " : "";
}

function remarkThoughtPreview(fallback: string) {
  return (tree: MdNode) => {
    tree.children = [
      { type: "text", value: markdownPlainText(tree).replace(/\s+/g, " ").trim() || fallback },
    ];
  };
}

/**
 * Thinking inside a tool group has its own disclosure, preserved across
 * recycling. A group whose row already reads "Thought" (no visible tool) skips
 * the header.
 */
export function ReasoningTraceBlock({
  messages,
  live,
  showHeader,
}: {
  messages: ReadonlyArray<Extract<MessagesTimelineRow, { kind: "message" }>["message"]>;
  live: boolean;
  showHeader: boolean;
}) {
  const ctx = use(TimelineRowCtx);
  const { isWorking, unsettledTurnId } = use(TimelineRowActivityCtx);
  const first = messages[0]!;
  const expanded = !showHeader || ctx.expandedReasoningMessageIds.has(first.id);
  const streaming =
    live &&
    messages.some((reasoningMessage) => reasoningMessage.streaming) &&
    isWorking &&
    first.turnId !== null &&
    first.turnId === unsettledTurnId;
  if (
    messages.every((reasoningMessage) => reasoningMessage.text.trim().length === 0) &&
    !streaming
  ) {
    return null;
  }
  const label = streaming ? "Thinking" : "Thought";
  const collapsedPreview = messages.find((message) => message.text.trim().length > 0)?.text.trim();
  const headerText = expanded ? (
    label
  ) : (
    <ReactMarkdown remarkPlugins={[remarkGfm, [remarkThoughtPreview, label]]}>
      {collapsedPreview ?? label}
    </ReactMarkdown>
  );
  return (
    <div className="flex flex-col">
      {showHeader ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => ctx.onToggleReasoning(first.id, !expanded)}
          className="flex min-h-6 cursor-pointer select-none items-center gap-1.5 rounded-md px-0.5 text-start text-sm leading-relaxed transition-colors hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"
        >
          <span className="flex size-6 shrink-0 items-center justify-center text-icon-muted">
            <BrainIcon aria-hidden className="block size-4 shrink-0 stroke-2 opacity-70" />
          </span>
          <span
            ref={streaming ? observeVisibleAnimation : undefined}
            className="relative min-w-0 flex-1 truncate text-secondary-label"
          >
            {headerText}
            {streaming ? <ActivityShimmerOverlay>{headerText}</ActivityShimmerOverlay> : null}
          </span>
          <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
            <ChevronRightIcon
              className={cn(
                "size-3 shrink-0 text-icon-muted opacity-70 transition-transform duration-200",
                expanded && "rotate-90",
              )}
            />
          </span>
        </button>
      ) : null}
      {expanded ? (
        <div className="ms-7 flex max-h-96 flex-col gap-3 overflow-auto px-0.5 py-1 select-text">
          {messages.map((reasoningMessage) => (
            <ChatMarkdown
              key={reasoningMessage.id}
              text={reasoningMessage.text}
              cwd={ctx.markdownCwd}
              threadKey={ctx.routeThreadKey}
              skills={ctx.providerSkills}
              isStreaming={streaming && reasoningMessage.streaming}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A provider's thinking trace. Collapsed by default: reasoning is context for
 * the answer, not the answer. The open/closed flag lives on the list so it
 * survives row recycling in the virtualizer.
 */
export function ReasoningTimelineRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "message" }>;
}) {
  const ctx = use(TimelineRowCtx);
  const { message } = row;
  const expanded = ctx.expandedReasoningMessageIds.has(message.id);
  const { onToggleReasoning } = ctx;
  const toggle = () => {
    onToggleReasoning(message.id, !expanded);
  };

  if (message.text.trim().length === 0) {
    return null;
  }

  return (
    <div className={cn("flex flex-col", expanded && "mb-1")}>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={toggle}
        className="flex cursor-pointer select-none items-center gap-1.5 rounded-md px-0.5 py-0.5 text-start transition-colors hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"
      >
        <span className="flex size-6 shrink-0 items-center justify-center text-icon-muted">
          <BrainIcon aria-hidden className="block size-4 shrink-0 stroke-2 opacity-70" />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="relative min-w-0 flex-1 truncate text-secondary-label text-sm leading-relaxed">
            Thought
          </span>
          <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
            <ChevronRightIcon
              className={cn(
                "size-3 shrink-0 text-icon-muted opacity-70 transition-transform duration-200",
                expanded && "rotate-90",
              )}
            />
          </span>
        </span>
      </button>
      {expanded ? (
        <div className="mt-1 ms-7 flex max-h-96 flex-col gap-3 overflow-auto px-0.5 py-1 select-text">
          <ChatMarkdown
            text={message.text}
            cwd={ctx.markdownCwd}
            threadKey={ctx.routeThreadKey}
            skills={ctx.providerSkills}
          />
        </div>
      ) : null}
    </div>
  );
}
